import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import {useEffect} from "react";
import {useForm} from "react-hook-form";

import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Sheet, SheetContent, SheetFooter} from "@/components/ui/sheet";
import {
  FORM_SHEET_FOOTER_CLASS,
  FORM_SHEET_ROOT_FORM_CLASS,
} from "@/src/modules/core/lib/form-sheet-layout";
import {SheetFormScrollBody} from "@/src/modules/help/components/sheet-form-scroll-body";
import {SheetHelpHeader} from "@/src/modules/help/components/sheet-help-header";
import useInviteHouseholdOccupant from "@/src/modules/households/hooks/use-invite-household-occupant";
import {
  inviteHouseholdOccupantSchema,
  type InviteHouseholdOccupantInput,
} from "@/src/modules/households/schemas/invite-household-occupant";

type InviteTenantSheetProps = {
  open: boolean;
  householdId: number;
  contextPropertyId: number;
  onClose: () => void;
};

export function InviteTenantSheet({
  open,
  householdId,
  contextPropertyId,
  onClose,
}: InviteTenantSheetProps) {
  const {mutateAsync: inviteOccupant, isPending} = useInviteHouseholdOccupant({
    onSuccess: onClose,
  });

  const form = useForm<InviteHouseholdOccupantInput>({
    resolver: zodResolver(inviteHouseholdOccupantSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
      first_name: "",
      last_name: "",
    },
  });
  const values = form.watch();
  const hasAllRequiredFields =
    Boolean(values.email?.trim()) &&
    Boolean(values.first_name?.trim()) &&
    Boolean(values.last_name?.trim());

  useEffect(() => {
    if (!open) {
      form.reset({
        email: "",
        first_name: "",
        last_name: "",
      });
    }
  }, [open, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    await inviteOccupant({
      id: householdId,
      body: {
        email: values.email.trim(),
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        context_property_id: contextPropertyId,
      },
    });
  });

  return (
    <Sheet
      open={open}
      askConfirmation={() => Object.keys(form.formState.dirtyFields).length > 0}
      preventClose={isPending}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent className="flex min-h-0 flex-col gap-0 overflow-hidden p-0">
        <SheetHelpHeader title="Invite tenant" />
        <Form {...form}>
          <form onSubmit={handleSubmit} noValidate className={FORM_SHEET_ROOT_FORM_CLASS}>
            <SheetFormScrollBody className="flex flex-col gap-4 px-4 pt-2 pb-4">
              <p className="text-muted-foreground text-sm">
                The tenant will get access to this unit after they accept.
              </p>

              <FormField
                control={form.control}
                name="first_name"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="given-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="last_name"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="family-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({field}) => (
                  <FormItem>
                    <FormLabel required>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SheetFormScrollBody>

            <SheetFooter className={FORM_SHEET_FOOTER_CLASS}>
              <Button type="submit" className="w-full" disabled={isPending || !hasAllRequiredFields}>
                Send invitation
                {isPending ? <Loader2 className="ml-2 size-4 animate-spin" /> : null}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
